import React, { useState, useEffect } from "react";
import { Route, Flag, CheckCircle, Circle, ChevronRight, Edit, Save, Trash2, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Roadmap, RoadmapStep } from "@/types/database-extensions";

const RoadmapBuilder = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [roadmaps, setRoadmaps] = useState<Roadmap[]>([]);
  const [activeRoadmap, setActiveRoadmap] = useState<Roadmap | null>(null);
  const [selectedStep, setSelectedStep] = useState<RoadmapStep | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newRoadmap, setNewRoadmap] = useState({
    title: "",
    description: "",
  });
  const [editingStep, setEditingStep] = useState(false);
  const [newResource, setNewResource] = useState({ name: "", url: "" });

  useEffect(() => {
    if (user) {
      fetchRoadmaps();
    }
  }, [user]);

  const fetchRoadmaps = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('roadmaps')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        // Check if the error is due to table not existing
        if (error.message?.includes('relation "public.roadmaps" does not exist') || 
            error.code === 'PGRST116' || 
            error.message?.includes('does not exist')) {
          // Silently handle missing table - no error message to user
          console.log("Roadmaps table doesn't exist yet");
          setRoadmaps([]);
          setActiveRoadmap(null);
          return;
        }
        throw error;
      }
      
      if (Array.isArray(data) && data.length > 0) {
        setRoadmaps(data);
        setActiveRoadmap(data[0]);
      } else {
        setRoadmaps([]);
        setActiveRoadmap(null);
      }
    } catch (error) {
      console.error("Error fetching roadmaps:", error);
      // Only show error for unexpected issues, not missing table
      if (!error.message?.includes('does not exist')) {
        toast({
          title: "Error",
          description: "Could not load your roadmaps",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const createRoadmap = async () => {
    if (!user) return;
    if (!newRoadmap.title.trim()) {
      toast({
        title: "Missing Title",
        description: "Please provide a title for your roadmap",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('roadmaps')
        .insert([{
          title: newRoadmap.title,
          description: newRoadmap.description,
          user_id: user.id,
          progress: 0,
          steps: []
        }])
        .select()
        .single();
        
      if (error) throw error;
      
      const newRoadmapData = data as Roadmap;
      
      if (newRoadmapData) {
        setRoadmaps([...roadmaps, newRoadmapData]);
        setActiveRoadmap(newRoadmapData);
        setCreating(false);
        setNewRoadmap({ title: "", description: "" });
        
        // Update karma points
        await updateKarmaPoints(10);
        
        toast({
          title: "Roadmap Created",
          description: "Your new roadmap has been created",
        });
      }
    } catch (error) {
      console.error("Error creating roadmap:", error);
      toast({
        title: "Error",
        description: "Could not create your roadmap",
        variant: "destructive"
      });
    }
  };

  const addStep = async () => {
    if (!activeRoadmap || !user) return;
    
    try {
      const newStep: RoadmapStep = {
        id: crypto.randomUUID(),
        roadmap_id: activeRoadmap.id,
        title: "New Step",
        name: "New Step",
        status: "not_started" as 'not_started',
        order: activeRoadmap.steps ? activeRoadmap.steps.length : 0,
        completed: false,
        current: !activeRoadmap.steps || activeRoadmap.steps.length === 0,
        description: "Add a description for this step",
        resources: []
      };
      
      const updatedSteps = [...(activeRoadmap.steps || []), newStep];
      
      const { error } = await supabase
        .from('roadmaps')
        .update({
          steps: updatedSteps
        })
        .eq('id', activeRoadmap.id)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      const updatedRoadmap = {
        ...activeRoadmap,
        steps: updatedSteps
      };
      
      setActiveRoadmap(updatedRoadmap);
      setRoadmaps(roadmaps.map(r => r.id === activeRoadmap.id ? updatedRoadmap : r));
      setSelectedStep(newStep);
      setEditingStep(true);
      
      toast({
        title: "Step Added",
        description: "New step has been added to your roadmap",
      });
    } catch (error) {
      console.error("Error adding step:", error);
      toast({
        title: "Error",
        description: "Could not add step to your roadmap",
        variant: "destructive"
      });
    }
  };

  const updateStep = async (updatedStep: RoadmapStep) => {
    if (!activeRoadmap || !selectedStep || !user) return;
    
    try {
      // Ensure resources is properly typed
      const stepWithProperResources = {
        ...updatedStep,
        resources: Array.isArray(updatedStep.resources) ? updatedStep.resources : []
      };
      
      const updatedSteps = activeRoadmap.steps?.map(step => 
        step.id === stepWithProperResources.id ? stepWithProperResources : step
      ) || [];
      
      const { error } = await supabase
        .from('roadmaps')
        .update({
          steps: updatedSteps
        })
        .eq('id', activeRoadmap.id)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      const updatedRoadmap = {
        ...activeRoadmap,
        steps: updatedSteps
      };
      
      setActiveRoadmap(updatedRoadmap);
      setRoadmaps(roadmaps.map(r => r.id === activeRoadmap.id ? updatedRoadmap : r));
      setSelectedStep(stepWithProperResources);
      setEditingStep(false);
      
      toast({
        title: "Step Updated",
        description: "Your roadmap step has been updated",
      });
    } catch (error) {
      console.error("Error updating step:", error);
      toast({
        title: "Error",
        description: "Could not update your roadmap step",
        variant: "destructive"
      });
    }
  };

  const markStepComplete = async (stepId: string) => {
    if (!activeRoadmap || !user) return;
    
    try {
      let currentStepIndex = -1;
      const updatedSteps = activeRoadmap.steps?.map((step, index) => {
        if (step.id === stepId) {
          currentStepIndex = index;
          return { ...step, completed: true, current: false, status: 'completed' as 'completed' };
        }
        return step;
      }) || [];
      
      // Set the next step as current
      if (currentStepIndex !== -1 && currentStepIndex < updatedSteps.length - 1) {
        updatedSteps[currentStepIndex + 1].current = true;
        updatedSteps[currentStepIndex + 1].status = 'in_progress' as 'in_progress';
      }
      
      // Calculate new progress
      const completedCount = updatedSteps.filter(step => step.completed).length;
      const newProgress = updatedSteps.length > 0 ? Math.floor((completedCount / updatedSteps.length) * 100) : 0;
      
      const { error } = await supabase
        .from('roadmaps')
        .update({ 
          steps: updatedSteps,
          progress: newProgress
        })
        .eq('id', activeRoadmap.id)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      const updatedRoadmap = {
        ...activeRoadmap,
        steps: updatedSteps,
        progress: newProgress
      };
      
      setActiveRoadmap(updatedRoadmap);
      setRoadmaps(roadmaps.map(r => r.id === activeRoadmap.id ? updatedRoadmap : r));
      setSelectedStep(null);
      
      // Add karma points
      await updateKarmaPoints(5);
      
      toast({
        title: "Step Completed!",
        description: "Great job! You're making progress on your roadmap.",
        variant: "default"
      });
    } catch (error) {
      console.error("Error marking step as complete:", error);
      toast({
        title: "Error",
        description: "Could not update your progress",
        variant: "destructive"
      });
    }
  };

  const deleteRoadmap = async (roadmapId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('roadmaps')
        .delete()
        .eq('id', roadmapId)
        .eq('user_id', user.id);
        
      if (error) throw error;
      
      const updatedRoadmaps = roadmaps.filter(r => r.id !== roadmapId);
      setRoadmaps(updatedRoadmaps);
      
      if (activeRoadmap?.id === roadmapId) {
        setActiveRoadmap(updatedRoadmaps.length > 0 ? updatedRoadmaps[0] : null);
        setSelectedStep(null);
      }
      
      toast({
        title: "Roadmap Deleted",
        description: "Your roadmap has been deleted",
      });
    } catch (error) {
      console.error("Error deleting roadmap:", error);
      toast({
        title: "Error",
        description: "Could not delete your roadmap",
        variant: "destructive"
      });
    }
  };

  const addResource = () => {
    if (!selectedStep || !newResource.name || !newResource.url) return;
    
    const currentResources = selectedStep.resources || [];
    const updatedResources = [...currentResources, { name: newResource.name, url: newResource.url }];
    const updatedStep = { ...selectedStep, resources: updatedResources };
    
    updateStep(updatedStep);
    setNewResource({ name: "", url: "" });
  };

  const viewStepDetails = (step: RoadmapStep) => {
    setSelectedStep(step);
  };

  // Update user karma points
  const updateKarmaPoints = async (points: number) => {
    if (!user) return;
    
    try {
      // Get current karma points
      const { data, error } = await supabase
        .from('profiles')
        .select('karma_points')
        .eq('id', user.id)
        .single();
      
      if (error) throw error;
      
      const currentPoints = data?.karma_points || 0;
      const newPoints = currentPoints + points;
      
      // Update karma points
      await supabase
        .from('profiles')
        .update({ karma_points: newPoints })
        .eq('id', user.id);
        
      // Show toast for karma points
      toast({
        title: `+${points} Karma Points!`,
        description: "Keep up the good work!",
      });
    } catch (error) {
      console.error("Error updating karma points:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (creating) {
    return (
      <div className="space-y-4">
        <button 
          onClick={() => setCreating(false)} 
          className="flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
          Back to Roadmaps
        </button>
        
        <div className="space-y-4">
          <h3 className="font-medium">Create New Roadmap</h3>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input 
              value={newRoadmap.title}
              onChange={e => setNewRoadmap({...newRoadmap, title: e.target.value})}
              placeholder="Roadmap title (e.g., Become an Entrepreneur)"
            />
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea 
              value={newRoadmap.description}
              onChange={e => setNewRoadmap({...newRoadmap, description: e.target.value})}
              placeholder="Brief description of your roadmap goal"
              rows={3}
            />
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createRoadmap}>Create Roadmap</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!activeRoadmap) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Route className="h-12 w-12 text-primary mb-4" />
        <h3 className="font-medium text-lg mb-2">No Roadmaps Yet</h3>
        <p className="text-muted-foreground mb-4">Create your first roadmap to get started on your journey!</p>
        <Button onClick={() => setCreating(true)}>Create Roadmap</Button>
      </div>
    );
  }

  if (selectedStep) {
    return (
      <div className="space-y-4">
        <button 
          onClick={() => {
            setSelectedStep(null);
            setEditingStep(false);
          }}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
          Back to Roadmap
        </button>
        
        {editingStep ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Step Name</label>
              <Input 
                value={selectedStep.name || selectedStep.title || ''}
                onChange={e => setSelectedStep({
                  ...selectedStep, 
                  name: e.target.value,
                  title: e.target.value
                })}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea 
                value={selectedStep.description || ""}
                onChange={e => setSelectedStep({...selectedStep, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Resources</label>
              {selectedStep.resources && selectedStep.resources.length > 0 ? (
                <ul className="space-y-1 ml-5 list-disc">
                  {selectedStep.resources.map((resource, index) => (
                    <li key={index} className="text-sm">
                      {resource.name} - <span className="text-muted-foreground">{resource.url}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No resources added yet</p>
              )}
              
              <div className="flex gap-2 mt-2">
                <Input 
                  value={newResource.name}
                  onChange={e => setNewResource({...newResource, name: e.target.value})}
                  placeholder="Resource name"
                  className="text-sm"
                />
                <Input 
                  value={newResource.url}
                  onChange={e => setNewResource({...newResource, url: e.target.value})}
                  placeholder="URL"
                  className="text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={addResource}
                  disabled={!newResource.name || !newResource.url}
                >
                  Add
                </Button>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setEditingStep(false);
                  setSelectedStep(activeRoadmap.steps?.find(s => s.id === selectedStep.id) || null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={() => updateStep(selectedStep)}>
                <Save className="h-4 w-4 mr-1" />
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              {selectedStep.completed ? (
                <CheckCircle className="h-6 w-6 text-green-500" />
              ) : selectedStep.current ? (
                <Flag className="h-6 w-6 text-amber-500" />
              ) : (
                <Circle className="h-6 w-6 text-muted-foreground" />
              )}
              <h3 className="font-medium text-lg">{selectedStep.name || selectedStep.title}</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                className="ml-auto"
                onClick={() => setEditingStep(true)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
            
            {selectedStep.description && (
              <p className="text-muted-foreground">
                {selectedStep.description}
              </p>
            )}
            
            {selectedStep.resources && selectedStep.resources.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Helpful Resources:</h4>
                <ul className="space-y-1 ml-5 list-disc">
                  {selectedStep.resources.map((resource, index) => (
                    <li key={index}>
                      <a href={resource.url} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                        {resource.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {selectedStep.current && !selectedStep.completed && (
              <Button 
                className="mt-4 w-full" 
                onClick={() => markStepComplete(selectedStep.id)}
              >
                Mark as Completed
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Route className="h-5 w-5 text-primary" />
          <div className="flex items-center space-x-2">
            <h3 className="font-medium">{activeRoadmap.title}</h3>
            <Button variant="ghost" size="sm" className="h-6 p-1" onClick={() => setCreating(true)}>
              <PlusCircle className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {roadmaps.length > 1 && (
          <select 
            className="text-sm bg-background border rounded px-2 py-1"
            value={activeRoadmap.id}
            onChange={e => {
              const selected = roadmaps.find(r => r.id === e.target.value);
              if (selected) setActiveRoadmap(selected);
            }}
          >
            {roadmaps.map(roadmap => (
              <option key={roadmap.id} value={roadmap.id}>{roadmap.title}</option>
            ))}
          </select>
        )}
      </div>
      
      <div className="mb-3">
        <div className="flex justify-between text-xs mb-1">
          <span>Overall Progress</span>
          <span>{activeRoadmap.progress || 0}%</span>
        </div>
        <Progress value={activeRoadmap.progress || 0} className="h-2" />
      </div>
      
      {activeRoadmap.steps && activeRoadmap.steps.length > 0 ? (
        <ul className="space-y-2 mb-4">
          {activeRoadmap.steps.map((step) => (
            <li key={step.id} className="flex items-center gap-2">
              <div className="flex-shrink-0">
                {step.completed ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : step.current ? (
                  <Flag className="h-5 w-5 text-amber-500" />
                ) : (
                  <Circle className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm ${step.current ? "font-medium" : step.completed ? "text-muted-foreground" : ""}`}>
                  {step.name || step.title}
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 rounded-full"
                onClick={() => viewStepDetails(step)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center py-6 text-muted-foreground">
          <p>No steps in this roadmap yet. Add your first step to get started!</p>
        </div>
      )}
      
      <div className="flex gap-2">
        <Button 
          className="w-full" 
          size="sm" 
          onClick={addStep}
        >
          Add Step
        </Button>
        
        <Button
          variant="destructive"
          size="sm"
          onClick={() => deleteRoadmap(activeRoadmap.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default RoadmapBuilder;
